/** Data access for `customers` (scoped to the workspace by API rules). */
import { isLocale, type Locale } from "@/i18n/languages";
import type { CustomersResponse } from "@/lib/core/pb-types";
import { pb } from "@/lib/core/pocketbase";
import { sortBy, type SortDirection } from "./sort";
import { currentWorkspaceId } from "./workspaces";

export const CUSTOMERS_PAGE_SIZE = 25;

/** Editable customer fields; "" means "not set" (see the customer_details migration). */
export type CustomerInput = {
  type: "business" | "private";
  /** Company name; for private customers the server derives it from first + last name. */
  name: string;
  contactPerson: string;
  firstName: string;
  lastName: string;
  email: string;
  phone: string;
  street: string;
  addressLine2: string;
  postalCode: string;
  city: string;
  /** ISO 3166-1 alpha-2 */
  country: string;
  vatId: string;
  /** Whole days as text; "" uses the invoice template's payment terms. */
  paymentTermsDays: string;
  /** ISO 4217; "" uses the template's currency. */
  currency: string;
  /** Label language for this customer's invoices; "" keeps the template's labels. */
  invoiceLanguage: "" | Locale;
  /** "" = automatic (ZUGFeRD for businesses in Germany), see lib/invoice/einvoice.ts. */
  einvoiceFormat: "" | "pdf" | "zugferd" | "xrechnung";
  /** Buyer reference / Leitweg-ID (public authorities). */
  buyerReference: string;
};

export const EMPTY_CUSTOMER: CustomerInput = {
  type: "business",
  name: "",
  contactPerson: "",
  firstName: "",
  lastName: "",
  email: "",
  phone: "",
  street: "",
  addressLine2: "",
  postalCode: "",
  city: "",
  country: "",
  vatId: "",
  paymentTermsDays: "",
  currency: "",
  invoiceLanguage: "",
  einvoiceFormat: "",
  buyerReference: "",
};

/** Record -> form values (missing fields become ""). */
export function toCustomerInput(record: CustomersResponse): CustomerInput {
  const values = Object.fromEntries(
    Object.keys(EMPTY_CUSTOMER).map((key) => [key, record[key as keyof CustomersResponse] ?? ""]),
  ) as CustomerInput;
  return {
    ...values,
    type: record.type === "private" ? "private" : "business",
    invoiceLanguage: isLocale(record.invoiceLanguage) ? record.invoiceLanguage : "",
  };
}

const inWorkspace = () =>
  pb.filter("workspace = {:workspace}", { workspace: currentWorkspaceId() });

/** URL sort keys of the customer list -> PocketBase fields (the address column sorts by city). */
const SORT_FIELDS: Record<string, string> = { name: "name", address: "city", email: "email" };

export type CustomerListParams = {
  page?: number;
  perPage?: number;
  sort?: string;
  direction?: SortDirection;
};

export async function listCustomers({
  page = 1,
  perPage = CUSTOMERS_PAGE_SIZE,
  sort,
  direction,
}: CustomerListParams = {}) {
  return pb.collection("customers").getList<CustomersResponse>(Math.max(1, page), perPage, {
    filter: inWorkspace(),
    sort: sortBy(SORT_FIELDS, sort, direction),
  });
}

/** All customers for pickers, alphabetical. */
export async function listAllCustomers(): Promise<CustomersResponse[]> {
  return pb.collection("customers").getFullList<CustomersResponse>({
    filter: inWorkspace(),
    sort: "name",
  });
}

export async function getCustomer(id: string): Promise<CustomersResponse> {
  return pb.collection("customers").getOne<CustomersResponse>(id);
}

export async function createCustomer(input: CustomerInput): Promise<CustomersResponse> {
  return pb
    .collection("customers")
    .create<CustomersResponse>({ ...input, workspace: currentWorkspaceId() });
}

export async function updateCustomer(id: string, input: CustomerInput) {
  return pb.collection("customers").update<CustomersResponse>(id, input);
}

export async function deleteCustomer(id: string): Promise<void> {
  await pb.collection("customers").delete(id);
}

/**
 * Imports mapped CSV rows (lib/customer/csv-import.ts) in one go: the server creates them in a
 * single transaction, skips duplicates of existing customers and checks the plan's limit.
 */
export async function importCustomers(
  customers: CustomerInput[],
): Promise<{ created: number; skipped: number[] }> {
  return pb.send("/api/blanqo/customers/import", {
    method: "POST",
    body: { workspace: currentWorkspaceId(), customers },
  });
}
